import { AxiosInstance } from "axios"
import {
    CheckInData,
    MeetingAudience,
    Resource,
    ServiceCenterBooking,
    ServiceCenterGuest,
    ServiceCenterGuestState,
    ServiceCenterNews,
} from "./serviceCenterHomeTypes"
import { ServiceResponse } from "../../../../core/api/apiTypes"
import { GuestCardDetails } from "../guestCardTypes"
import { DeviceDetails } from "../../../entities/devices/deviceTypes"
import { ApiRoomDisplay, DeviceTemplate } from "../../../office/admin/organization/administrators/deviceTypes"

export enum ServiceCenterHomeQuery {
    SERVICE_CENTER_MEETINGS = "service-center-meetings",
    SERVICE_CENTER_GUESTS = "service-center-guests",
    SERVICE_CENTER_NEWS = "service-center-news",
    SERVICE_CENTER_RESOURCES = "service-center-resources",
    SERVICE_CENTER_DEVICE_TEMPLATES = "service-center-device-templates",
}

type FetchServiceCenterMeetingsParams = Readonly<{
    serviceCenterId: number
    startDate?: string
    endDate?: string
    audience?: MeetingAudience
}>

export type ServiceCenterMeetingsResponse = Readonly<{
    orders: Array<ServiceCenterBooking>
}>

export async function fetchServiceCenterMeetings(
    { serviceCenterId, startDate, endDate, audience }: FetchServiceCenterMeetingsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterMeetingsResponse> {
    const response = await axios.get(`/serviceCenters/${serviceCenterId}/bookings`, {
        params: {
            startDate,
            endDate,
            audience,
        },
        signal,
    })
    return response.data
}

type FetchServiceCenterGuestsParams = Readonly<{
    serviceCenterId: number
    startDate?: string
    endDate?: string
}>

export type ServiceCenterGuestsResponse = Readonly<{
    guests: Array<ServiceCenterGuest>
}>

export async function fetchServiceCenterGuests(
    { serviceCenterId, startDate, endDate }: FetchServiceCenterGuestsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterGuestsResponse> {
    const response = await axios.get(`/serviceCenters/${serviceCenterId}/guests`, {
        params: {
            startDate,
            endDate,
            state: ServiceCenterGuestState.PENDING,
        },
        signal,
    })
    return response.data
}

type FetchNewsParams = Readonly<{
    locationId: number
    category?: string
    offset?: number
    limit?: number
}>

export type FetchNewsResponse = Readonly<{
    news: Array<ServiceCenterNews>
    pagination: {
        nextLink: {
            rel: string
            href: string
        }
    }
}>

export async function fetchNews(
    { locationId, ...params }: FetchNewsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchNewsResponse> {
    const response = await axios.get(`/news/${locationId}`, {
        params: {
            ...params,
        },
        signal,
    })
    return response.data
}

export type CheckInGuestParams = Readonly<{
    serviceCenterId: number
    checkIns: CheckInData[]
    notifyHost?: boolean
    messageToHost?: string
}>

export type CheckInHost = Readonly<{
    id: number
    displayName: string
    email?: string
    phoneNumber?: string
    mobileNumber?: string
    userGroupId?: number
    companyId: number
    companyName: string
}>

export type CheckInGuestResponse = ServiceResponse<{
    guestCardDetails?: GuestCardDetails
    messageToGuest?: string
    host?: CheckInHost
    messageResponse?: { guestMessage: string; type: "INFO" | "WARNING" | "ERROR" }
}>

export async function checkInGuest(
    { serviceCenterId, ...rest }: CheckInGuestParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CheckInGuestResponse> {
    const { data } = await axios.post(
        `/serviceCenters/${serviceCenterId}/guests/batch/checkIn`,
        {
            ...rest,
        },
        {
            signal,
        },
    )

    return data
}

export type CheckOutGuestParams = Readonly<{
    serviceCenterId: number
    attendees: number[]
}>

export async function checkOutGuest(
    { serviceCenterId, attendees }: CheckOutGuestParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.post(
        `/serviceCenters/${serviceCenterId}/guests/batch/checkOut`,
        {
            attendees,
        },
        {
            signal,
        },
    )

    return data
}

export type ServiceCenterResourcesResponse = Readonly<{
    resources: Resource[]
}>

export type ServiceCenterResourcesParams = Readonly<{
    serviceCenterId: number
    type?: string
}>

export async function fetchServiceCenterResources(
    { serviceCenterId, type }: ServiceCenterResourcesParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterResourcesResponse> {
    const { data } = await axios.get(`/serviceCenters/${serviceCenterId}/resources?type=${type}'`, {
        signal,
    })

    return data
}

export type DeviseTemplatesParams = Readonly<{
    serviceCenterId: number
    type?: string
}>

export type ServiceCenterDeviceTemplatesResponse = Readonly<{
    deviceTemplates: Array<DeviceTemplate>
}>

export async function fetchServiceCenterDeviceTemplates(
    { serviceCenterId, ...rest }: DeviseTemplatesParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterDeviceTemplatesResponse> {
    const { data } = await axios.get(`/serviceCenters/${serviceCenterId}/deviceTemplates`, {
        signal,
        params: {
            ...rest,
        },
    })

    return data
}

// --- Create device ---
type CreateAdminParams = Readonly<{
    admin: DeviceDetails
}>

type AdminResponse = Readonly<{
    device: DeviceDetails
}>

type CreateAdminResponse = ServiceResponse<AdminResponse>

export async function createDevice(
    { admin }: CreateAdminParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CreateAdminResponse> {
    const response = await axios.post(
        `/devices`,
        {
            admin,
        },
        {
            signal,
        },
    )

    return response.data
}

// --- Update device ---
type UpdateAdminParams = Readonly<{
    userId: number
    admin: DeviceDetails
}>

type UpdateAdminResponse = ServiceResponse<AdminResponse>

export async function updateDevice(
    { userId, admin }: UpdateAdminParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<UpdateAdminResponse> {
    const url = `/devices/${userId}`

    const response = await axios.post(
        url,
        {
            admin,
        },
        {
            signal,
        },
    )

    return response.data
}

// --- Create room device ---
type CreateRoomDeviceParams = Readonly<{
    device: ApiRoomDisplay
}>

type RoomDeviceResponse = Readonly<{
    device: ApiRoomDisplay
}>

type CreateRoomDeviceResponse = ServiceResponse<RoomDeviceResponse>

export async function createRoomDevice(
    { device }: CreateRoomDeviceParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CreateRoomDeviceResponse> {
    const response = await axios.post(
        `/devices`,
        {
            device,
        },
        {
            signal,
        },
    )

    return response.data
}

// --- Update room device ---
type UpdateRoomDeviceParams = Readonly<{
    deviceId: number
    device: ApiRoomDisplay
}>

type UpdateRoomDeviceResponse = ServiceResponse<RoomDeviceResponse>

export async function updateRoomDisplayDevice(
    { deviceId, device }: UpdateRoomDeviceParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<UpdateRoomDeviceResponse> {
    const url = `/devices/${deviceId}`

    const response = await axios.post(
        url,
        {
            device,
        },
        {
            signal,
        },
    )

    return response.data
}
